import type { ReportQuery } from '@/shared/api/resources';

/**
 * O filtro dos relatorios vive no endereco (?year=2026&month=3...): a pagina com o
 * recorte pode ser guardada e enviada, e trocar de aba mantem o periodo, como as
 * segmentacoes de Mes e Ano do cabecalho comum do OnPoint.
 */

/** Valores unicos do filtro (pesquisas, seletor de metrica, numero, responsavel, regra). */
const UNICOS = [
  'broker_id',
  'policyholder_id',
  'economic_group',
  'metric',
  'number',
  'owner_member_id',
  'rule',
] as const;

/** Listas de texto do filtro (situacoes e segmentacoes das paginas). */
const LISTAS = [
  'status',
  'policy_status',
  'pipe',
  'proposal_type',
  'endorsement_status',
  'step',
  'final_result',
  'rule_status',
] as const;

export type ChaveDeLista = 'year' | 'month' | (typeof LISTAS)[number];
export type ChaveUnica = (typeof UNICOS)[number];

/** Le o filtro do endereco. */
export function lerFiltro(busca: URLSearchParams): ReportQuery {
  const filtro: ReportQuery = {};
  const anos = busca.getAll('year').map(Number).filter(Number.isInteger);
  const meses = busca.getAll('month').map(Number).filter(Number.isInteger);
  if (anos.length > 0) filtro.year = anos;
  if (meses.length > 0) filtro.month = meses;
  for (const chave of LISTAS) {
    const valores = busca.getAll(chave);
    if (valores.length > 0) filtro[chave] = valores;
  }
  for (const chave of UNICOS) {
    const valor = busca.get(chave);
    if (valor) filtro[chave] = valor;
  }
  return filtro;
}

/** Informa se o parametro e uma lista do filtro. */
export function ehChaveDeLista(chave: string): chave is ChaveDeLista {
  return (
    chave === 'year' ||
    chave === 'month' ||
    (LISTAS as readonly string[]).includes(chave)
  );
}

/** Informa se o parametro e um valor unico do filtro. */
export function ehChaveUnica(chave: string): chave is ChaveUnica {
  return (UNICOS as readonly string[]).includes(chave);
}

/** Liga ou desliga um valor de uma lista do filtro. */
export function alternarNaLista(
  busca: URLSearchParams,
  chave: ChaveDeLista,
  valor: string,
): URLSearchParams {
  const proxima = new URLSearchParams(busca);
  const atuais = proxima.getAll(chave);
  proxima.delete(chave);
  const novos = atuais.includes(valor)
    ? atuais.filter((atual) => atual !== valor)
    : [...atuais, valor];
  for (const item of novos) proxima.append(chave, item);
  return proxima;
}

/** Troca um valor unico do filtro; vazio tira. */
export function trocarUnico(
  busca: URLSearchParams,
  chave: ChaveUnica,
  valor: string | null,
): URLSearchParams {
  const proxima = new URLSearchParams(busca);
  if (valor) proxima.set(chave, valor);
  else proxima.delete(chave);
  return proxima;
}

/** Anos oferecidos na segmentacao: os cinco ultimos, do mais recente. */
export function anosOferecidos(hoje: Date = new Date()): number[] {
  const atual = hoje.getFullYear();
  return [0, 1, 2, 3, 4].map((recuo) => atual - recuo);
}

export const MESES = [
  'Jan',
  'Fev',
  'Mar',
  'Abr',
  'Mai',
  'Jun',
  'Jul',
  'Ago',
  'Set',
  'Out',
  'Nov',
  'Dez',
];
